import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { NotifiableSettingType, NotificationSettingResponse, NotificationSettingType } from '@business/entities/notification_settings';
import { handleMessage, requestMessage } from '@business/messages';
import { Session } from '@modules/auth/session';
import { map, tap } from 'rxjs/operators';
import { NotificationType } from '@business/entities/notifications';
import { NotificationSettingsAPI } from '@business/api/notification_settings';
import { UserId } from '@business/entities/account';
import { CompanyId } from '@business/entities/companies';
import { TeamId } from '@business/entities/team';
import { of } from 'rxjs';

export class NotificationSettingsBloc extends BaseBloc {
  private readonly notificationSettingsRepo = new Repository<NotificationSettingResponse>({
    getItemId: setting => `${setting.notifiableType}-${setting.notifiableId}-${setting.type}`,
  });

  readonly items$ = this.notificationSettingsRepo.items$;
  readonly notificationSettings = this.notificationSettingsRepo.items;
  readonly selectItem = this.notificationSettingsRepo.selectItem;

  readonly companySettings$ = this.notificationSettingsRepo.items$.pipe(
    map(items => items.filter(i => i.notifiableType === NotifiableSettingType.COMPANY)),
  );
  readonly userSettings$ = this.notificationSettingsRepo.items$.pipe(
    map(items => items.filter(i => i.notifiableType === NotifiableSettingType.USER)),
  );
  readonly teamSettings$ = this.notificationSettingsRepo.items$.pipe(
    map(items => items.filter(i => i.notifiableType === NotifiableSettingType.TEAM)),
  );

  readonly companyAppSettings$ = this.companySettings$.pipe(map(items => items.filter(i => i.type === NotificationSettingType.APP)));
  readonly userAppSettings$ = this.userSettings$.pipe(map(items => items.filter(i => i.type === NotificationSettingType.APP)));
  readonly teamAppSettings$ = this.teamSettings$.pipe(map(items => items.filter(i => i.type === NotificationSettingType.APP)));

  readonly companyEmailSettings$ = this.companySettings$.pipe(map(items => items.filter(i => i.type === NotificationSettingType.EMAIL)));
  readonly userEmailSettings$ = this.userSettings$.pipe(map(items => items.filter(i => i.type === NotificationSettingType.EMAIL)));
  readonly teamEmailSettings$ = this.teamSettings$.pipe(map(items => items.filter(i => i.type === NotificationSettingType.EMAIL)));

  onReset() {
    this.notificationSettingsRepo.reset();
  }

  onInit() {
    // Initialize with empty state - settings will be loaded on demand
  }

  fetchNotificationSettings = (notifiableType: NotifiableSettingType, notifiableId: string, type?: NotificationSettingType) => {
    return NotificationSettingsAPI.fetchNotificationSettings(notifiableType, notifiableId, type).pipe(
      this.notificationSettingsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_notification_settings_error') }),
    );
  };

  updateNotificationSettings = (
    notifiableType: NotifiableSettingType,
    notifiableId: string,
    type: NotificationSettingType,
    settings: { [key: string]: boolean },
    showSuccessMessage: boolean = true,
  ) => {
    return NotificationSettingsAPI.updateNotificationSettings(notifiableType, notifiableId, type, settings).pipe(
      this.notificationSettingsRepo.ops.upsertOne(item => ({ item })),
      tap(() => {
        if (showSuccessMessage) {
          handleMessage({
            type: requestMessage('update_notification_settings'),
          })(of(null)).subscribe();
        }
      }),
    );
  };

  // Helper method to get current user's settings
  getUserSettings = (userId: UserId, type: NotificationSettingType) => {
    return this.fetchNotificationSettings(NotifiableSettingType.USER, userId, type);
  };

  // Helper method to update current user's settings
  updateUserSettings = (userId: UserId, type: NotificationSettingType, settings: { [key: string]: boolean }) => {
    return this.updateNotificationSettings(NotifiableSettingType.USER, userId, type, settings);
  };

  // Helper method to get company settings
  getCompanySettings = (companyId: CompanyId, type: NotificationSettingType) => {
    return this.fetchNotificationSettings(NotifiableSettingType.COMPANY, companyId, type);
  };

  // Helper method to update company settings
  updateCompanySettings = (companyId: CompanyId, type: NotificationSettingType, settings: { [key: string]: boolean }) => {
    return this.updateNotificationSettings(NotifiableSettingType.COMPANY, companyId, type, settings);
  };

  // Helper method to get team settings
  getTeamSettings = (teamId: TeamId, type: NotificationSettingType) => {
    return this.fetchNotificationSettings(NotifiableSettingType.TEAM, teamId, type);
  };

  // Helper method to update team settings
  updateTeamSettings = (teamId: TeamId, type: NotificationSettingType, settings: { [key: string]: boolean }) => {
    return this.updateNotificationSettings(NotifiableSettingType.TEAM, teamId, type, settings);
  };
}
